import React, { useEffect, useState } from "react";
import { markOnboardingStep } from "../lib/api";

/**
 * OnboardingWelcomeModal — first-sign-in greeter.
 *
 * Shown when the signed-in user hasn't completed the `welcome_seen` step
 * AND hasn't dismissed onboarding. Dismissing the modal calls
 * `/api/onboarding/step` with `welcome_seen` so it never re-appears.
 *
 * Props:
 *   open: bool
 *   onClose: fn
 *   onTour?: fn  — placeholder for future interactive tour (Phase 35+)
 */
const STEPS = [
  {
    icon: "fa-gamepad",
    title: "Pick your main game",
    body: "Set a primary game so the right leagues, players, and tournaments show up first.",
    color: "text-[#14b8a6] border-[#14b8a6]/40 bg-[#14b8a6]/10",
  },
  {
    icon: "fa-user-astronaut",
    title: "Choose your avatar",
    body: "Pick a preset, generate one with AI, or upload your own. This is your face on the ladder.",
    color: "text-[#14b8a6] border-[#14b8a6]/40 bg-[#14b8a6]/10",
  },
  {
    icon: "fa-tower-broadcast",
    title: "Join your first league",
    body: "Browse public leagues from the LEAGUES tab, or get an invite link from a friend.",
    color: "text-[#14b8a6] border-[#14b8a6]/40 bg-[#14b8a6]/10",
  },
  {
    icon: "fa-comments",
    title: "Say hi in chat",
    body: "Every league has a live chat. Tag teammates with @name to make sure they see it.",
    color: "text-[#14b8a6] border-[#14b8a6]/40 bg-[#14b8a6]/10",
  },
];

export function OnboardingWelcomeModal({ open, onClose, userName }) {
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    // ESC to close
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") finish(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const finish = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await markOnboardingStep("welcome_seen");
    } catch (_e) { /* non-blocking — UI still closes */ }
    setBusy(false);
    onClose?.();
  };

  return (
    <div
      data-testid="onboarding-welcome-modal"
      className="fixed inset-0 z-[140] bg-black/90 backdrop-blur-md p-4 flex items-center justify-center"
      onClick={(e) => e.target === e.currentTarget && finish()}
    >
      <div className="glass-strong rounded-3xl w-full max-w-2xl p-6 sm:p-8 border-2 border-[#14b8a6]/50 relative overflow-hidden">
        {/* Animated neon bg */}
        <div className="absolute inset-0 -z-10 opacity-30 bg-gradient-to-br from-[#14b8a6]/40 via-transparent to-[#14b8a6]/30" />

        <p className="text-[10px] tracking-[4px] text-[#14b8a6] font-bold">
          <i className="fas fa-rocket mr-1" />WELCOME TO LEAGUE HUB
        </p>
        <h2 className="font-display text-3xl sm:text-4xl mt-2 leading-tight">
          {userName ? <>Hey <span className="text-[#14b8a6]">{userName}</span>,</> : "Hey operator,"}<br />
          let's get you set up.
        </h2>
        <p className="text-sm text-white/65 mt-3">
          Four quick wins and you'll be on the ladder. Skip any time — your checklist will wait on the dashboard.
        </p>

        <ul className="mt-6 space-y-3" data-testid="onboarding-welcome-steps">
          {STEPS.map((s, i) => (
            <li
              key={s.title}
              className={`flex items-start gap-3 rounded-2xl border p-3 ${s.color}`}
              style={{ animation: `slideIn 0.4s ease-out ${i * 0.08}s both` }}
            >
              <div className="w-10 h-10 rounded-xl bg-black/40 flex items-center justify-center text-lg shrink-0">
                <i className={`fas ${s.icon}`} aria-hidden="true" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-white">{i + 1}. {s.title}</p>
                <p className="text-[12px] text-white/70 mt-0.5">{s.body}</p>
              </div>
              <span className="text-[9px] tracking-widest text-white/40 font-bold pt-1">{i + 1}/4</span>
            </li>
          ))}
        </ul>

        <div className="mt-6 flex flex-col sm:flex-row gap-2 justify-end">
          <button
            type="button"
            onClick={finish}
            disabled={busy}
            data-testid="onboarding-welcome-skip"
            className="text-[11px] tracking-widest px-4 py-2 rounded-xl border border-white/15 text-white/60 hover:text-white"
          >
            SKIP
          </button>
          <button
            type="button"
            onClick={finish}
            disabled={busy}
            data-testid="onboarding-welcome-start"
            className="btn-neon rounded-xl px-6 py-3 text-xs font-bold tracking-widest"
          >
            <i className="fas fa-bolt mr-1" />{busy ? "LOADING…" : "LET'S GO"}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(-8px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}

export default OnboardingWelcomeModal;
